import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';

import { colorIdentidad, colorRayo, getRayo, ORDEN_ENTIDADES, FAMILIAS, hermanasDe } from '../config/rayos';
import { useModo } from '../config/modo';
import { useTranslation } from 'react-i18next';
import { EN_EL_TABLERO } from '../config/alcance';
import { IMAGENES_VOZ } from '../config/imagenes';
import Emblema from '../components/Emblema';
import CabeceraSeccion from '../components/CabeceraSeccion';
import { nombreVoz, textoVoz } from '../config/nombres_voz';
import { esTdL } from '../config/destinatario';
import { useCatalog } from '../content/hooks';
import { ENSENANZAS, DESPEDIDA } from '../config/ensenanzas';
import './voces.css';

/**
 * LAS VOCES — ahora en DOS PÁGINAS (Faina, 26-ago: «Voces se va a dividir en el
 * menu en: Esenios y Angeles & Arcangeles. Dejala con la misma estructura»).
 *
 * Es la misma página con la misma ficha; lo único que cambia es qué familia
 * enseña. Y la división no es de menú: son dos naturalezas distintas. Los
 * esenios vivieron una vida humana —por eso llevan RETRATO y comparten el
 * naranja de la Fundación—; los ángeles no tienen rostro —por eso llevan
 * SÍMBOLO, cada uno con su rayo—. La familia decide las dos cosas.
 *
 * Sin familia, enseña las catorce: la ruta /voces sigue existiendo y es la que
 * usan los enlaces antiguos.
 *
 * Cada voz se presenta con SU FIRMA REAL, la que cierra sus canalizaciones. No
 * hay ni una línea escrita sobre ellas que no salga de sus propios textos.
 */





/* El titulo de cada seccion. Las tres pasan por aqui: si mañana cambia uno,
   cambia en su pagina y en las pestañas de las otras dos a la vez. */
const TITULO = { esenios: 'menu.esenios', angeles: 'menu.angeles', tdl: 'menu.tdl' };

const VocesPage = ({ familia }) => {
  const { t } = useTranslation();
  const { modo } = useModo();
  const f = familia ? FAMILIAS[familia] : null;
  const { items: catalogo } = useCatalog();

  /**
   * Una voz sin piezas publicadas NO se esconde: se enseña diciendo cuántas
   * vienen. Los Esenios son la quinta voz esenia y tienen 9 en el tablero, todas
   * por escribir — ocultarla sería hacer creer que no existe.
   */
  const voces = useMemo(() => {
    /* En TdL las fichas no son las voces de una familia, sino LAS ENTIDADES QUE
       LES HABLAN: se calculan del contenido, y en cuanto se publique una pieza
       TdL de otra voz su ficha aparece sola. */
    const soloTdL = familia === 'tdl';
    const fuente = soloTdL ? catalogo.filter(esTdL) : catalogo;
    const lista = soloTdL ? ORDEN_ENTIDADES : (f ? f.entidades : ORDEN_ENTIDADES);
    return lista.map((entidad) => {
      const suyas = fuente.filter((c) => c.entidad === entidad);
      return {
        entidad,
        n: suyas.length,
        enCamino: familia === 'tdl' ? 0 : (EN_EL_TABLERO[entidad] || 0),
        firma: suyas[0] && suyas[0].firma ? suyas[0].firma : ''
      };
    }).filter((v) => v.n > 0 || v.enCamino > 0);
  }, [f, familia, catalogo]);


  return (
    <Box className="voces">
      {/* SIN BAJADA en las tres (Faina, 26-ago). La de Esenios se retiro y las
          otras dos se homologan: el titulo en naranja dice donde estas y las
          otras dos al lado dicen que hay mas. */}
      <CabeceraSeccion titulo={TITULO[familia] ? t(TITULO[familia]) : t('voces.catorce')} seccion={familia || null} />

      <Container maxWidth="lg">
        <Box className="voces-lista">
          {voces.map(({ entidad, n, enCamino, firma }) => {
            const soloIcono = familia === 'tdl';
            const rayo = getRayo(entidad);
            return (
              <RouterLink
                key={entidad}
                to={familia === 'tdl'
                  ? `/canalizaciones?q=${encodeURIComponent('trabajadores de la luz')}&voz=${encodeURIComponent(entidad)}`
                  : (n > 0 ? `/canalizaciones?voz=${encodeURIComponent(entidad)}` : '/canalizaciones')}
                className={`voces-ficha${n === 0 ? ' voces-ficha-espera' : ''}${familia === 'tdl' ? ' voces-ficha-solo' : ''}`}
                style={{
                  '--rayo': colorIdentidad(entidad, modo),
                  '--rayo-texto': colorRayo(entidad, modo)
                }}
              >
                <span className="voces-luz">
                  {IMAGENES_VOZ[entidad]
                    ? <img src={IMAGENES_VOZ[entidad]} alt="" className="voces-img" />
                    : <Emblema entidad={entidad} tamano={104} modo={modo} />}
                </span>

                <span className="voces-cuerpo">
                  {/* 🟢 En Trabajadores de la Luz la ficha lleva solo el ícono y el
                      número (Faina, 21-10-2026): sin nombre, sin relación y sin
                      firma. */}
                  {!soloIcono && (<>
                  <span className="voces-nombre">{entidad === 'María Magdalena' ? t('voz.corto_magdalena') : nombreVoz(entidad, t)}</span>
                  {/* El renglon que DISTINGUE: en los esenios el parentesco, en
                      los angeles su atributo — lo que esa voz trata. Cada
                      familia pone aqui lo unico que separa a sus voces entre si.
                      El nombre del rayo queda de ultimo recurso: el color ya se
                      ve en el emblema, escribirlo no aportaba nada. */}
                  <span className="voces-rayo">
                    {textoVoz('voz.rel_', entidad, t, rayo.nombre)}
                  </span>
                  {/* Su firma real; y si la voz aun no tiene piezas, la que
                      esta anclada en su corpus: ninguna ficha se queda sin
                      cierre. */}
                  {(firma || entidad === 'Los Esenios') && (
                    <span className="voces-firma">«{firma || t('voz.firma_esenios')}»</span>
                  )}
                  </>)}
                  <span className={`voces-mas${n === 0 ? ' voces-mas-espera' : ''}`}>
                    {n > 0
                      /* Sin flecha detrás del número (Faina, 21-10-2026). */
                      ? <>{n} {n === 1 ? t('filtros.una') : t('filtros.varias')}</>
                      : <>{t('voces.en_camino', { n: enCamino })}</>}
                  </span>
                </span>
              </RouterLink>
            );
          })}
        </Box>

      </Container>

      {/* ALGUNAS ENSEÑANZAS — en las tres (Faina, 26-ago).
          El porque de cada una, en config/ensenanzas.js. */}
      {familia && ENSENANZAS[familia] && (
        <Box className="voces-cierre">
          <Container maxWidth="lg">
            <Typography className="voces-cierre-rotulo">{t('voces.algunas')}</Typography>

            <Box className="voces-mensajes">
              {/* El texto que VE el lector sale de la tabla de traduccion (claves
                  voces.m.<familia>.<n>_t / _x, Faina 21-sep: «traducirlas ahora»);
                  config/ensenanzas.js sigue siendo la fuente de verdad del espanol
                  y de la procedencia (fuente) de cada cita. */}
              {ENSENANZAS[familia].map((m, i) => (
                <Box className="voces-mensaje" key={m.clave}>
                  <Typography className="voces-mensaje-clave">{t(`voces.m.${familia}.${i + 1}_t`)}</Typography>
                  <Typography className="voces-mensaje-texto">{t(`voces.m.${familia}.${i + 1}_x`)}</Typography>
                </Box>
              ))}
            </Box>

            {/* Solo los esenios tienen una comunidad que se despide. */}
            {DESPEDIDA[familia] && (
              <>
                <Typography className="voces-cierre-despedida">
                  {t(`voces.desp.${familia}_texto`)}
                </Typography>
                <Typography className="voces-cierre-firma">{t(`voces.desp.${familia}_firma`)}</Typography>
              </>
            )}
          </Container>
        </Box>
      )}

    </Box>
  );
};

export default VocesPage;
